/**
 * Style resolution (basestyle inheritance) and unit conversion. All editor
 * layout derives from the file's own settings + style indents via these
 * helpers — never from hardcoded CSS.
 */
import type { Align, DocSettings, ResolvedStyle, StyleDef } from "./types";

/** 0.1mm units → CSS px at 96 dpi. */
export function unitsToPx(units: number): number {
  return (units * 0.1) / 25.4 * 96;
}

/** 0.1mm units → mm. */
export function unitsToMm(units: number): number {
  return units * 0.1;
}

const DEFAULT_FONT = "Courier Screenplay";
const DEFAULT_SIZE = 12;

function parseAlign(v: string | null): Align | undefined {
  if (v === "left" || v === "center" || v === "right") return v;
  return undefined;
}

function num(v: string | null): number | undefined {
  if (v == null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Build the style Map from a `<styles>` element. */
export function parseStyles(stylesEl: Element): Map<string, StyleDef> {
  const map = new Map<string, StyleDef>();
  const styleEls = stylesEl.getElementsByTagName("style");
  for (let i = 0; i < styleEls.length; i++) {
    const el = styleEls[i]!;
    const name = el.getAttribute("name");
    if (!name) continue;
    const def: StyleDef = {
      name,
      basestyle: el.getAttribute("basestyle") ?? undefined,
      styleEnter: el.getAttribute("style_enter") ?? undefined,
      styleTabAfter: el.getAttribute("style_tab_after") ?? undefined,
      styleTabBefore: el.getAttribute("style_tab_before") ?? undefined,
      font: el.getAttribute("font") ?? undefined,
      size: num(el.getAttribute("size")),
      spacebefore: num(el.getAttribute("spacebefore")),
      leftindent: num(el.getAttribute("leftindent")),
      rightindent: num(el.getAttribute("rightindent")),
      align: parseAlign(el.getAttribute("align")),
      allcaps: el.getAttribute("allcaps") === "1",
      keepwithnext: el.getAttribute("keepwithnext") === "1",
      builtin: el.getAttribute("builtin") === "1",
      builtinIndex: num(el.getAttribute("builtin_index")),
    };
    map.set(name, def);
  }
  return map;
}

/**
 * Resolve a style name to concrete layout properties, walking the basestyle
 * chain for inherited font/size. Flow attributes (style_enter/tab_*) come from
 * the style itself, not the base. Unknown styles fall back to Normal Text-ish
 * defaults so custom styles never crash the editor.
 */
export function resolveStyle(
  styles: Map<string, StyleDef>,
  name: string,
): ResolvedStyle {
  const chain: StyleDef[] = [];
  let cur = styles.get(name);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.name)) {
    seen.add(cur.name);
    chain.push(cur);
    cur = cur.basestyle ? styles.get(cur.basestyle) : undefined;
  }

  // Walk from the style itself up the chain; first defined value wins.
  const pick = <K extends keyof StyleDef>(key: K): StyleDef[K] | undefined => {
    for (const s of chain) {
      const v = s[key];
      if (v !== undefined) return v;
    }
    return undefined;
  };

  const self = chain[0];
  return {
    name,
    font: pick("font") ?? DEFAULT_FONT,
    size: pick("size") ?? DEFAULT_SIZE,
    spacebefore: self?.spacebefore ?? 0,
    leftindent: self?.leftindent ?? 0,
    rightindent: self?.rightindent ?? 0,
    align: self?.align ?? "left",
    allcaps: self?.allcaps ?? false,
    keepwithnext: self?.keepwithnext ?? false,
    styleEnter: self?.styleEnter,
    styleTabAfter: self?.styleTabAfter,
    styleTabBefore: self?.styleTabBefore,
  };
}

/** Parse `<settings>` geometry (only what layout needs). */
export function parseSettings(settingsEl: Element): DocSettings {
  const n = (attr: string, fallback: number): number =>
    num(settingsEl.getAttribute(attr)) ?? fallback;
  return {
    pageWidth: n("page_width", 2159),
    pageHeight: n("page_height", 2794),
    marginTop: n("margin_top", 266),
    marginBottom: n("margin_bottom", 228),
    marginLeft: n("margin_left", 317),
    marginRight: n("margin_right", 317),
    linesPerInch: n("normal_linesperinch", 6.0),
    elementSpacing: n("element_spacing", 1.0),
    sceneTimeSeparator: settingsEl.getAttribute("scene_time_separator") ?? " - ",
  };
}
